<template>
  <!-- App layout -->
  <div class="min-h-screen bg-gray-100 flex relative main-content">

    <!-- Main Content -->
    <main class="flex-1 p-6 overflow-y-auto page-container">
      <h1 class="text-2xl font-semibold mb-4">All Users</h1>
      <div class="bg-white p-4 rounded shadow">
        <div class="mb-4 flex items-center gap-2">
          <!-- Text search -->
          <el-input v-model="search" @input="onSearchChange" type="text" placeholder="Search by username or email…" class="search-input" />
          <!-- Filter dropdown toggle -->
          <el-dropdown>
            <el-button ref="btn" class="btn-filter">
              <i class="fa-solid fa-filter" />
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item :class="{active:!domainFilter}" @click="setDomainFilter(null)">All Domains </el-dropdown-item>
                <el-dropdown-item  
                  v-for="domain in domainOptionsWithAll" 
                  :key="domain.value" 
                  :class="{active:domainFilter?.value === domain.value} 
                  " @click="setDomainFilter(domain)"
                  >
                  {{ domain.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <!-- Scrollable Table -->
        <div class="overflow-auto max-h-table mb-4">
          <table class="min-w-full table-auto">
            <thead class="sticky top-0 bg-gray-200 z-10">
              <tr>
                <th class="px-4 py-2">ID</th>
                <th class="px-4 py-2">{{ $t('components.login.username') }}</th>
                <th class="px-4 py-2">{{ $t('components.login.email') }}</th>
                <th class="px-4 py-2">{{ $t('components.login.firstName') }}</th>
                <th class="px-4 py-2">{{ $t('components.login.lastName') }}</th>
                <th class="px-4 py-2">{{ $t('components.login.orderReceiveEmail') }}</th>
                <th class="px-4 py-2">{{ $t('components.login.domain') }}</th>
                <!-- <th class="px-4 py-2">{{ $t('components.login.role') }}</th> -->
                <th class="px-4 py-2">{{ $t('components.table.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.userId" class="hover:bg-gray-100 cursor-pointer">
                <td class="border px-4 py-2">{{ user.userId }}</td>
                <td class="border px-4 py-2">{{ user.username }}</td>
                <td class="border px-4 py-2">{{ user.email }}</td>
                <td class="border px-4 py-2">{{ user.firstname }}</td>
                <td class="border px-4 py-2">{{ user.lastname }}</td>
                <td class="border px-4 py-2">
                  {{ user.additionalEmails?.join(', ') || '-' }}
                </td>
                <td class="border px-4 py-2">{{ displayDomain(user.domainId) }}</td>
                <!-- TODOS: FIX THIS LATER -->
                <!-- <td class="border px-4 py-2">
                  {{ displaySupplier(user.supplierUserId)  }}
                </td> -->
                <!-- <td class="border px-4 py-2">
                  {{ user.role  }}
                </td> -->
                <td class="border px-4 py-2">
                  <div class="action-column">
                    <el-tooltip content="Change Supplier" placement="top">
                      <el-button @click="openDomainModal(user as User)">
                        <i class="fa-solid fa-building-user" />
                      </el-button>
                    </el-tooltip>
                    <el-tooltip content="Change Role" placement="top">
                      <el-button @click="openRoleModal(user as User)" :disabled="isUpdating">
                        <i class="fa-solid fa-user-gear" />
                      </el-button>
                    </el-tooltip>
                    <el-tooltip content="Delete User" placement="top">
                      <el-button @click="openDeleteUser(user as User)" :disabled="isDeleting">
                        <i class="fa-solid fa-trash" />
                      </el-button>
                    </el-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Under Table -->
        <div class="flex justify-between items-center gap-2">
          <div>
          <el-button @click="prevPage" :disabled="!hasPrev || isFetchingData"
            class="border rounded hover:bg-gray-100 disabled:opacity-50">Previous</el-button>
          <el-button @click="nextPage" :disabled="!hasNext || isFetchingData"
            class="border rounded hover:bg-gray-100 disabled:opacity-50">Next</el-button>
            </div>
          <span class="px-4 py-3">Page {{ currentPage }} of {{ totalPages }}</span>
        </div>
      </div>
    </main>

  <ChangeUserDomainModal
    ref="changeUserDomainModalRef"
    :detail-user="detailUser"
    :domain-options-with-all="domainOptionsWithAll"
    :close-detail-modal="closeDetailModal"
    :on-submit="updateUserDomain"
    :is-submitting="isUpdating"
  />
<!-- 
  <ChangeUserModal
    ref="changeRoleModalRef"
    :detail-user="detailUser"
    :detail-role="detailRole"
    @update:detailRole="(value)=>{
      detailRole = value
    }"
    :close-detail-modal="closeDetailModal"
    :save-detail-change="saveChangeRole"
  /> -->
    
  </div>
</template>

<script setup lang="ts">
  import {
    ref,
    computed,
    onMounted,
    watchEffect,
  } from 'vue'
  import {
    deleteUser,
    getUsersWithPaging,
    updateUserDomainId
  } from '@/services/api/userService'
import './styles/table.scss'
import ChangeUserDomainModal from './modals/ChangeUserDomainModal.vue';
import { UsersWithPaging } from '@/types/api/Raw/user';
import { defaultFirstCursor } from '@/types/constants/pagination';
import { DomainsWithPaging } from '@/types/api/Raw/domain';
import { FilterOption } from '@/types/api/Raw/pagination';
import { getDomainsWithPaging } from '@/services/api/domainService';
import { User } from '@/types/api/User/User';
import { ElMessage, ElMessageBox } from 'element-plus';
import i18n from '@/i18n';
import { apolloClient } from '@/services/api/api';

const itemsPerPage = 10
const userResponse = ref<UsersWithPaging | null>(null)
const users = ref<User[]>([])
const domainResponse = ref<DomainsWithPaging | null>(null)
const domainFilter = ref<FilterOption | null>(null)
const search = ref('')
const isFetching = ref(false)
const hasNext = ref(false)
const hasPrev = ref(false)
const isUpdating = ref(false)
const isDeleting = ref(false)
const totalPages = ref(1)
const currentPage = ref(1)
let searchTimeout = 0
const changeUserDomainModalRef = ref()
const changeRoleModalRef = ref()
const isFetchingData = ref(false)

/** Edit-modal */
const detailUser = ref<User | null>(null)

const onSearchChange = (value:string) => {
  clearTimeout(searchTimeout)

  searchTimeout = window.setTimeout(() => {
    fetchUsers({
      search: value,
      first: itemsPerPage,
      after: defaultFirstCursor
    })
  }, 1000);
  currentPage.value = 1

}

const nextPage = async ()=>{
  if(!userResponse.value?.pageInfo.endCursor)
    return

  isFetchingData.value = true

  try{
    await fetchUsers({
      first:itemsPerPage,
      after:userResponse.value?.pageInfo.endCursor,
    })
    currentPage.value++ 
  } catch (error) {
    console.error('Failed to fetch next page:', error)
  } finally {
    isFetchingData.value = false
  }
}
const prevPage = async ()=>{
  if(!userResponse.value?.pageInfo.startCursor)
    return
  isFetchingData.value = true
  try {
    await fetchUsers({
      last:itemsPerPage,
      before:userResponse.value?.pageInfo.startCursor,
    })
    currentPage.value--
  } catch (error) {
    console.error('Failed to fetch previous page:', error)
  }
  finally {
    isFetchingData.value = false
  }
}

const fetchUsers = async ({
  search,
  after,
  before,
  first,
  last,
  domainId,
}:{
  search?:string
  after?:string
  before?:string
  first?:number
  last?:number
  domainId?:number
}) => {
  try {
    isFetching.value = true
    const userPagingRes = await getUsersWithPaging({
      first,
      last,
      after,
      before,
      search,
      filter:{
        domainId
      }
    })
    userResponse.value = userPagingRes
    totalPages.value = Math.ceil((userPagingRes.totalCount ?? 1) / itemsPerPage)
    hasNext.value = userPagingRes?.pageInfo?.hasNextPage ?? false
    hasPrev.value = userPagingRes?.pageInfo?.hasPreviousPage ?? false
    isFetching.value = false
      
  } catch (error) {
    console.error('failed to fetch users')    
  }
}

const fetchDomains = async () => {
  try {
    const domainsPagingRes = await getDomainsWithPaging({
      first:30,
      after:defaultFirstCursor
    })
    domainResponse.value = domainsPagingRes
  } catch (error) {
    console.error('failed to fetch domains')    
  }
}

const domainOptionsWithAll = computed(()=>{
  const domains = domainResponse.value?.edges?.map((domain=>({label: domain.node.urlSuffix, value: domain.node.domainId})))
  return (domains ?? [])
})

watchEffect(() => {
  users.value = userResponse.value
    ? userResponse.value.edges.map(edge => edge.node)
    : []
})

const displayDomain = (domainId: number)=>{
  return domainOptionsWithAll.value.find(opt=>opt.value === domainId)?.label ?? '-'
}

const setDomainFilter = (domain:FilterOption | null)=>{
  domainFilter.value = domain
  fetchUsers({
    ...(domain?.value ? {domainId: domain?.value as number}:undefined),
    first: itemsPerPage,
    after: defaultFirstCursor
  })
}

function openRoleModal(u: User) {
  alert('In Progress')
  detailUser.value = u
  changeRoleModalRef.value?.openModal()
}

function openDeleteUser(u: User) {
    ElMessageBox.confirm(
        i18n.global.t('confirm.historyDelete.message') as string, 
        i18n.global.t('confirm.historyDelete.title') as string, {
          confirmButtonText: i18n.global.t('confirm.delete.ok') as string,
          cancelButtonText: i18n.global.t('confirm.delete.cancel') as string,
          type: 'warning',
        }
      )
      .then(async () => {
        isDeleting.value = true
        try {
          await deleteUser({userId:u.userId})
          ElMessage.success(i18n.global.t('success.api.delete') as string);
          await apolloClient.resetStore();
          await fetchUsers({
            search: search.value,
            first: itemsPerPage,
            after: defaultFirstCursor,
          ...(domainFilter?.value?.value ? {domainId: domainFilter?.value?.value as number}:undefined),
          })
        } catch (error) {
          ElMessage.error(i18n.global.t('error.api.delete') as string);
          console.error('[Failed to delete user]:', error);
        } finally{
          isDeleting.value = false
        }
      })
      .catch(() => {
      });
}

function openDomainModal(u: User) {
  detailUser.value = u
  changeUserDomainModalRef.value?.openModal()
}

function closeDetailModal() {
  changeUserDomainModalRef.value?.closeModal()
  changeRoleModalRef.value?.closeModal()
}

/** Save change */
async function updateUserDomain(domainId:number | null) {

  if(!detailUser.value) return
  isUpdating.value = true
  try {
    await updateUserDomainId({
      userId: detailUser.value.userId,
      domainId
    })
    await apolloClient.resetStore();
    await fetchUsers({
      search: search.value,
      first: itemsPerPage,
      after: defaultFirstCursor,
    ...(domainFilter?.value?.value ? {domainId: domainFilter?.value?.value as number}:undefined),
    })
    ElMessage({
      message: i18n.global.t('success.api.update'),
      type:  'success',
      showClose: true,
    });

    closeDetailModal()

    if(!domainId) return
    users.value = users.value.map((user)=>{
    if (user.userId === detailUser.value?.userId)
      return { ...user, domainId }
    
    return user
    })
  } catch (error) {
    ElMessage({
      message: i18n.global.t('error.api.server'),
      type:  'error',
      showClose: true,
    });
    console.error('failed to update user domain')    
  } finally{
    isUpdating.value = false
  }
}

onMounted(()=>{
  fetchDomains()

  fetchUsers({
    first: itemsPerPage,
    after: defaultFirstCursor
  })
})

</script>
